import {
  BodyMedium,
  BodySmall,
  CardBody,
  CardTitle,
  LinkButton,
  HeadingSmall,
  Modal,
  theme,
  useToast,
} from '@arnold/common';
import styled from '@emotion/styled/macro';
import { useRef, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDeleteOrganizationLogoMutation, useUploadOrganizationLogoMutation } from '../../generated/hooks';
import { convertToProcessedPng } from '../../lib/helpers/image';

const ALLOWED_MIMETYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

interface LogoData {
  id: string;
  url: string;
  originalName: string;
  createdAt: string;
}

interface IProps {
  organizationId: string;
  logo: LogoData | null | undefined;
}

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.f};
  margin-top: ${theme.spacing.g};
`;

const DropZone = styled.div<{ $dragOver: boolean; $hasLogo: boolean }>`
  width: 100%;
  border: 1px dashed
    ${({ $dragOver, $hasLogo }) =>
      $dragOver ? theme.colors.actionPrimary.default : $hasLogo ? 'transparent' : theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.e};
  padding: ${({ $hasLogo }) => ($hasLogo ? '12px' : '33px')};
  cursor: ${({ $hasLogo }) => ($hasLogo ? 'default' : 'pointer')};
  background-color: ${({ $dragOver, $hasLogo }) =>
    $dragOver ? theme.colors.backgroundCover.hover : $hasLogo ? 'transparent' : theme.colors.backgroundBasic.default};
  transition: all 0.2s ease;

  ${({ $hasLogo }) =>
    !$hasLogo &&
    `
    &:hover {
      border-color: ${theme.colors.actionPrimary.default};
      background-color: ${theme.colors.backgroundCover.hover};
    }
  `}
`;

const LogoPreviewWrapper = styled.div`
  width: 100px;
  height: 100px;
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.l};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LogoPreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const UploadIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.xl};
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${theme.colors.actionPrimary.default};
`;

const FileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const FileNameText = styled(BodyMedium)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BodySmallDisabled = styled(BodySmall)`
  color: ${theme.colors.text.disabled};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.d};
  align-items: center;
  flex-shrink: 0;
`;

const ErrorText = styled(BodySmall)`
  color: ${theme.colors.emotionDanger.default};
`;

const UploadIconSvg = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 11l5-5 5 5M12 6v10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LogoCard = ({ organizationId, logo }: IProps) => {
  const [t, i18n] = useTranslation('settings');
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [uploadLogo, { loading: uploading }] = useUploadOrganizationLogoMutation();
  const [deleteLogo, { loading: deleting }] = useDeleteOrganizationLogoMutation();

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });

  const validateFile = (file: File): Promise<string | null> =>
    new Promise((resolve) => {
      if (!ALLOWED_MIMETYPES.includes(file.type)) {
        resolve(t('logoErrorFormat'));
        return;
      }
      if (file.type === 'image/svg+xml') {
        resolve(null);
        return;
      }
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        if (img.width < 100 || img.height < 100) {
          resolve(t('logoErrorResolution'));
        } else {
          resolve(null);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(t('logoErrorFormat'));
      };
      img.src = url;
    });

  const handleFile = async (file: File) => {
    setError(null);
    const validationError = await validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      const processedFile = await convertToProcessedPng(file);
      await uploadLogo({
        variables: { organizationId, file: processedFile },
        refetchQueries: ['OrganizationSetting'],
        awaitRefetchQueries: true,
        context: { headers: { 'X-Apollo-Operation-Name': 'uploadOrganizationLogo' } },
      });
      addToast(t('logoUploadSuccess'));
    } catch {
      setError(t('logoUploadError'));
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDelete = async () => {
    try {
      await deleteLogo({
        variables: { organizationId },
        refetchQueries: ['OrganizationSetting'],
        awaitRefetchQueries: true,
      });
      setShowDeleteModal(false);
      addToast(t('logoDeleteSuccess'));
    } catch {
      setShowDeleteModal(false);
      setError(t('logoDeleteError'));
    }
  };

  return (
    <Card>
      <CardBody>
        <CardTitle>
          <HeadingSmall>{t('logoTitle')}</HeadingSmall>
          <BodySmall>{t('logoDescription')}</BodySmall>
        </CardTitle>
        <LogoContainer data-icom="organization-logo-container">
          <DropZone
            $dragOver={dragOver}
            $hasLogo={!!logo}
            data-icom="organization-logo-upload"
            onClick={logo ? undefined : () => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {logo ? (
              <>
                <LogoPreviewWrapper>
                  <LogoPreview src={logo.url} alt={logo.originalName} />
                </LogoPreviewWrapper>
                <FileInfo>
                  <FileNameText>{logo.originalName}</FileNameText>
                  <BodySmallDisabled>{t('logoUploadedOn', { date: formatDate(logo.createdAt) })}</BodySmallDisabled>
                </FileInfo>
                <ActionButtons onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline-primary"
                    data-icom="organization-logo-upload"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t('logoUploadButton')}
                  </Button>
                  <LinkButton
                    data-icom="organization-logo-delete"
                    color="danger"
                    disabled={deleting}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    {t('logoDeleteButton')}
                  </LinkButton>
                </ActionButtons>
              </>
            ) : (
              <>
                <UploadIconWrapper>
                  <UploadIconSvg />
                </UploadIconWrapper>
                <FileInfo>
                  <BodyMedium>{t('logoDragDropHint')}</BodyMedium>
                  <BodySmallDisabled>{t('logoFormatHint')}</BodySmallDisabled>
                </FileInfo>
                <ActionButtons onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline-primary"
                    data-icom="organization-logo-upload"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t('logoUploadButton')}
                  </Button>
                </ActionButtons>
              </>
            )}
          </DropZone>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/png,image/jpeg,image/svg+xml"
            onChange={handleFileInputChange}
          />
          {error && <ErrorText>{error}</ErrorText>}
        </LogoContainer>

        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onSubmit={handleDelete}
          title={t('logoDeleteTitle')}
          content={<BodyMedium>{t('logoDeleteMessage')}</BodyMedium>}
          buttons={{
            submit: { title: t('logoDeleteConfirm') },
            cancel: { title: t('logoDeleteCancel') },
          }}
        />
      </CardBody>
    </Card>
  );
};

export default LogoCard;
